/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable arrow-body-style */
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import { useDishCategoryFormConfig } from '@/features/menu-management/SidebarAccordion/forms/useDishCategoryFormConfig'

import { OptionType, TranslationItemModel } from '@/shared/api/BaseAPI.model'
import AccordionContent from '../AccordionContent/AccordionContent'
import { useDishSizesQuery } from '../../hooks/useDishSizesQuery'
import { useDishCategoriesQuery } from '../../hooks/useDishCategoriesQuery'
import { useSelectedLanguagesQuery } from '../../hooks/useSelectedLanguagesQuery'
import { useFormDirtyStore } from '@/shared/store/useFormDirtyStore'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { useImageSlots } from '@/shared/store/useImageSlots'
import IMAGE_SLOTS_KEYS from '@/shared/store/imageSlots.model'
import { CATEGORY_IMAGES_ENABLED } from '../../config'

interface DishSizesAccordionProps {
  accordionIndex: number
  openAccordion: number
  handleAccordionClick: (index: number) => void
}

function DishCategoriesAccordion(props: DishSizesAccordionProps) {
  const { accordionIndex, openAccordion, handleAccordionClick } = props
  const { t } = useTranslation()

  const [isModalOpen, setIsModalOpen] = useState(false)

  const [categoryId, setCategoryId] = useState<string>()
  const configuredCategoryIdRef = useRef<string>()
  // The submit awaits the save and the upload, so the modal stays open long
  // enough for a second click to create a second category.
  const isSubmittingRef = useRef(false)
  const resetIsDirty = useFormDirtyStore(state => state.reset)
  const { showSnackbar } = useSnackbar()

  const { dishSizesList } = useDishSizesQuery()
  const {
    dishCategoriesList,
    category,
    postCategoryAsync,
    putDishCategoryAsync,
    deleteCategory,
    categoryImage,
    postCategoryImageAsync,
    deleteCategoryImage
  } = useDishCategoriesQuery(categoryId)
  const clearImageSlot = useImageSlots(state => state.clear)

  const initialCategoryFormConfig = useDishCategoryFormConfig({
    imageQuery: categoryImage,
    // Only a saved category has a picture on the server to delete; for a new
    // one, dropping the slot is the whole job.
    onImageDelete: categoryId ? () => deleteCategoryImage() : undefined
  })
  const [updatedCategoryFormConfig, setUpdatedCategoryFormConfig] = useState(
    initialCategoryFormConfig
  )
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { selectedLanguagesList, defaultLanguage } =
    useSelectedLanguagesQuery(currentLanguage)

  const contentList = dishCategoriesList?.data?.map(item => {
    const updatedTranslations = selectedLanguagesList?.map(language => {
      const existingTranslation = item.translations.find(
        translation => translation.languageCode === language.languageCode
      )
      return (
        existingTranslation || {
          name: t('SIDEBAR_ACCORDION.MISSING_TRANSLATION'),
          languageCode: language.languageCode
        }
      )
    })
    const updatedRelatedItems = dishSizesList.data
      ?.filter(dishSize => item.dishSizeIds.includes(dishSize.id || ''))
      .map(
        dishSizeItem =>
          dishSizeItem.translations.find(
            translation =>
              translation.languageCode === defaultLanguage?.languageCode
          ) || {
            name: t('SIDEBAR_ACCORDION.MISSING_TRANSLATION'),
            languageCode: defaultLanguage?.languageCode || ''
          }
      )

    return {
      ...item,
      translations: updatedTranslations || [],
      relatedItems: updatedRelatedItems || []
    }
  })

  // The modal opens only from inside the effect above, on data. A failed load
  // therefore reads as a dead button, nothing opens and nothing is said, and
  // the row cannot even be retried, because `categoryId` stays set and clicking
  // the same row again is not a state change.
  useEffect(() => {
    if (!category.isError) return
    showSnackbar(
      t('ALERTS.LOAD_FOR_EDIT_ERROR.TITLE'),
      t('ALERTS.LOAD_FOR_EDIT_ERROR.DESCRIPTION'),
      'error'
    )
    configuredCategoryIdRef.current = undefined
    setCategoryId(undefined)
  }, [category.isError, showSnackbar, t])

  // Declared ahead of the submit handlers that call it, so it can stay an
  // arrow constant instead of a hoisted declaration (react/jsx-no-bind).
  const handleOnCloseModal = () => {
    // Abandoning the form drops the cropped picture too, or it is still in the
    // slot when the next category is opened.
    clearImageSlot(IMAGE_SLOTS_KEYS.CATEGORY_IMAGE)
    configuredCategoryIdRef.current = undefined
    setCategoryId(undefined)
    setIsModalOpen(false)
  }

  // The picture lives in an image slot, not in a form value: the cropper writes
  // a blob there and the category payload has no field for it. Read at submit
  // time rather than from a subscribed value, because the edit form's
  // `onSubmit` is built inside an effect that does not re-run on a crop.
  const uploadCategoryImageFromSlot = async (targetCategoryId: string) => {
    if (!CATEGORY_IMAGES_ENABLED) return
    const slot = useImageSlots.getState().slots[IMAGE_SLOTS_KEYS.CATEGORY_IMAGE]
    const blob = slot?.croppedBlob
    if (!blob) return
    const file = new File([blob], 'category-image', {
      type: slot?.mime || 'image/webp'
    })
    // The picture is a side request: its own onError already tells the user,
    // and letting it reject would leave the modal open on a saved category.
    await postCategoryImageAsync({
      file,
      categoryId: targetCategoryId
    }).catch(() => undefined)
  }

  const handleAddCategory = () => {
    const onSubmit = async (data: any) => {
      if (isSubmittingRef.current) return
      isSubmittingRef.current = true
      try {
        const translationsObj = data.translations ?? {}

        const languageCodes = Object.keys(translationsObj)
        const translationStrings = Object.values(translationsObj) as string[]

        const dishSizeIdsList: string[] =
          data.dishSizes?.map((dishSize: OptionType) => dishSize.value) ?? []

        const payload: {
          translations: TranslationItemModel[]
          dishSizeIds: string[]
        } = {
          translations: translationStrings.map((str, i) => ({
            name: str,
            languageCode: languageCodes[i]
          })),
          dishSizeIds: dishSizeIdsList
        }
        // POST must answer with the created id, the image route is keyed by a
        // category that does not exist yet at submit time. See config.ts.
        const created = await postCategoryAsync(payload)
        if (created?.id) await uploadCategoryImageFromSlot(created.id)
        resetIsDirty()
        handleOnCloseModal()
      } finally {
        isSubmittingRef.current = false
      }
    }
    const newFormConfig = {
      ...initialCategoryFormConfig,
      // Explicitly empty: spreading the initial config alone would leave the
      // `defaultValues` of the category edited before it in place, and the add
      // form would open pre-filled with that category.
      defaultValues: undefined,
      onSubmit
    }
    setUpdatedCategoryFormConfig(newFormConfig)
    setIsModalOpen(true)
  }

  const handleEditCategory = (categoryId: string) => {
    setCategoryId(categoryId)
  }

  useEffect(() => {
    if (
      category.data &&
      categoryId &&
      categoryId !== configuredCategoryIdRef.current
    ) {
      configuredCategoryIdRef.current = categoryId

      const onSubmit = async (data: any) => {
        if (isSubmittingRef.current) return
        isSubmittingRef.current = true
        try {
          const translationsObj = data.translations ?? {}

          const languageCodes = Object.keys(translationsObj)
          const translationStrings = Object.values(translationsObj) as string[]

          const dishSizeIdsList: string[] =
            data.dishSizes?.map((dishSize: OptionType) => dishSize.value) ?? []

          const payload: {
            translations: TranslationItemModel[]
            dishSizeIds: string[]
          } = {
            translations: translationStrings.map((str, i) => ({
              name: str,
              languageCode: languageCodes[i]
            })),
            dishSizeIds: dishSizeIdsList
          }

          // Nothing else closed the modal: it used to sit there after a save,
          // which reads as the edit having done nothing. Awaited rather than
          // fired and forgotten, so a failed save keeps the form and everything
          // typed into it. Closing here skips AccordionItemModal's close path,
          // the only place that clears the dirty flag, so that is done here too -
          // otherwise the next navigation warns about unsaved changes.
          await putDishCategoryAsync({ categoryId, categoryPayload: payload })
          await uploadCategoryImageFromSlot(categoryId)
          resetIsDirty()
          handleOnCloseModal()
        } finally {
          isSubmittingRef.current = false
        }
      }

      const selectedLanguageListCodeNames = selectedLanguagesList?.map(
        language => language.languageCode
      )

      const categoryLanguageList = category.data.translations.filter(
        translation =>
          selectedLanguageListCodeNames?.includes(translation.languageCode)
      )

      const defaultLanguageValue =
        categoryLanguageList.find(
          defaultLanguage =>
            defaultLanguage.languageCode === defaultLanguage?.languageCode
        )?.name || ''

      const defaultValues = {
        [defaultLanguage?.languageCode || '']: defaultLanguageValue,
        translations: {
          ...categoryLanguageList?.reduce((acc: any, translation) => {
            acc[translation.languageCode] = translation.name
            return acc
          }, {})
        },
        dishSizes: dishSizesList.data
          ?.filter(dishSize =>
            category.data.dishSizeIds.includes(dishSize.id || '')
          )
          .map(dishSize => ({
            label: dishSize.translations.find(
              translation =>
                translation.languageCode === defaultLanguage?.languageCode
            )?.name,
            value: dishSize.id
          }))
      }

      const newFormConfig = {
        ...initialCategoryFormConfig,
        defaultValues,
        onSubmit
      }

      setUpdatedCategoryFormConfig(newFormConfig)
      setIsModalOpen(true)
    }
  }, [category.data, categoryId, resetIsDirty])

  return (
    <AccordionContent
      title={t('SIDEBAR_ACCORDION.CATEGORIES.TITLE')}
      modalTitle={t('SIDEBAR_ACCORDION.CATEGORIES.MODAL_TITLE')}
      accordionIndex={accordionIndex}
      openAccordion={openAccordion}
      handleAccordionClick={handleAccordionClick}
      contentList={contentList || []}
      deleteFunction={deleteCategory}
      deleteMessage={t('SIDEBAR_ACCORDION.CATEGORIES.DELETE_MESSAGE')}
      loadingItemId={category.isLoading ? categoryId : undefined}
      accordionItemFormConfig={updatedCategoryFormConfig}
      handleAddAccordionItem={handleAddCategory}
      handleEditAccordionItem={handleEditCategory}
      handleOnCloseModal={handleOnCloseModal}
      isModalOpen={isModalOpen}
    />
  )
}
export default DishCategoriesAccordion
